import { Currency } from '@ps/web-core-enums';
import { DebugKey, createDebug } from '@ps/web-core-utils';
import type { History } from 'history';
import { debounce, set } from 'lodash';
import React, { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import type { Subscription } from 'rxjs';
import { fromEvent } from 'rxjs';
import type { NodeEventHandler } from 'rxjs/internal/observable/fromEvent';
import URL from 'url-parse';

import type { Dispatch } from '@swf/core/store';
import { select } from '@swf/core/store';
import { deprecate } from '@swf/core/utils';
import { fetchJson } from '@swf/core/utils/api';

import { routeHandler } from '../utils/c2l';

let subscriptions: Subscription[] = [];

enum CasinoHandHistoryRequestType {
  NUMBER_OF_HANDS = 1,
  NUMBER_OF_SECONDS = 2,
  HAND_ID = 3
}

const debug = createDebug(DebugKey.Embedded);

const attach = (name: string, handler: NodeEventHandler) =>
  fromEvent(document, name).subscribe((event) => {
    debug(name);
    handler(event);
  });

export const HANDLE_LOGIN_DEBOUNCE_MS = 300;

const AuthProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const isApplicationPaused = useRef(false);
  const isEmbedded = useSelector(select.device.isEmbedded);
  const {
    auth: { verifySession, logout },
    currency: { set: setCurrency }
  } = useDispatch<Dispatch>();
  const history: History = useHistory();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleLogin = useCallback(
    debounce(async () => {
      if (isApplicationPaused.current) {
        debug('handleLogin called whilst application is paused. Ignoring');
        return;
      }

      await verifySession();
    }, HANDLE_LOGIN_DEBOUNCE_MS),
    [verifySession]
  );

  const handlePlayMoney = useCallback(() => {
    setCurrency({ selected: window?.starsweb?.app?.playMoney ? Currency.PlayMoney : undefined });
  }, [setCurrency]);

  const handleLogout = useCallback(() => logout({ implicit: true }), [logout]);

  const handleNavigate = useCallback(
    ({ detail }: CustomEvent<{ payload: { reference: string } }>) => {
      routeHandler(detail.payload.reference, history);
    },
    [history]
  );

  useEffect(() => {
    subscriptions = [attach('starsnavigate', handleNavigate)];
    if (!isEmbedded) return;
    subscriptions = [
      ...subscriptions,
      ...[
        attach('starsplaymoney', handlePlayMoney),
        attach('starslogin', handleLogin),
        attach('starslogout', handleLogout),
        attach('starsready', () => {
          isApplicationPaused.current = false;
          handlePlayMoney();
          handleLogin();
        }),
        attach('starsresume', () => {
          isApplicationPaused.current = false;
          handlePlayMoney();
          handleLogin();
        }),
        attach('starspause', () => {
          isApplicationPaused.current = true;
        })
      ]
    ];

    return () => subscriptions.forEach((subscription) => subscription.unsubscribe());
  }, [handleLogin, handleLogout, handleNavigate, handlePlayMoney, isEmbedded]);

  useEffect(() => {
    if (isEmbedded) {
      handlePlayMoney();
    }
  }, [handlePlayMoney, isEmbedded]);

  // TODO remove getHandHistory and useEffect once mobile team finished removing ps.clb.getAppRef
  const getHandHistory = useCallback(
    async (
      locale: number,
      value: number,
      requestType: CasinoHandHistoryRequestType,
      requestValue: string
    ) => {
      deprecate('"getHandHistory" is deprecated and will be removed.');
      const url = new URL('/api/v0/history/casinoHandHistory');
      url.set('query', {
        requestType: requestType.toString(),
        locale: locale.toString(),
        numOfSeconds: (requestType === CasinoHandHistoryRequestType.NUMBER_OF_SECONDS
          ? requestValue
          : 0
        ).toString(),
        numOfHands: (requestType === CasinoHandHistoryRequestType.NUMBER_OF_HANDS
          ? requestValue
          : 0
        ).toString(),
        handId: (requestType === CasinoHandHistoryRequestType.HAND_ID
          ? requestValue
          : 0
        ).toString(),
        sendByEmail: 'true'
      });
      await fetchJson(url.toString(), { credentials: 'include' });
    },
    []
  );

  useEffect(() => {
    const customNoop = (name: string) => deprecate(`${name} is deprecated and will be removed.`);
    set(window, 'ps.clb.getAppRef', () => {
      deprecate('"ps.clb.getAppRef" is deprecated and will be removed.');
      return {
        getHandHistory,
        configure: customNoop.bind(null, 'ps.clb.getAppRef().configure'),
        balanceInfo: customNoop.bind(null, 'ps.clb.getAppRef().balanceInfo')
      };
    });
  }, [getHandHistory]);

  return React.createElement(React.Fragment, null, children);
};

export default AuthProvider;
